import { AnalyticsTrackingEvent } from "./events.js";
import { BrandclubEventType } from "./types.js";
const timeoutPromise = (ms, name, fn) => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      reject(new Error(`${name} timed out after ${ms}ms`));
    }, ms);
    fn().then(resolve, reject);
  });
};
function mapAppEventToExternalCustomEvent(bcEventName, supportedEventsConfig) {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  if (!supportedEventsConfig) {
    return void 0;
  }
  if (((_a = supportedEventsConfig.pageView) == null ? void 0 : _a.enabled) && ((bcEventName == null ? void 0 : bcEventName.endsWith("page view")) || (bcEventName == null ? void 0 : bcEventName.endsWith("bc_checkout_page_view")))) {
    return {
      eventName: supportedEventsConfig.pageView.mappedEventName,
      eventType: BrandclubEventType.PageView
    };
  }
  if (((_b = supportedEventsConfig.navigateToRetailer) == null ? void 0 : _b.enabled) && (bcEventName == null ? void 0 : bcEventName.endsWith("openRetailerProductPage"))) {
    return {
      eventName: supportedEventsConfig.navigateToRetailer.mappedEventName,
      eventType: BrandclubEventType.NavigateToRetailer
    };
  }
  if (((_c = supportedEventsConfig.addToCart) == null ? void 0 : _c.enabled) && (bcEventName == null ? void 0 : bcEventName.endsWith(":addToCart"))) {
    return {
      eventName: supportedEventsConfig.addToCart.mappedEventName,
      eventType: BrandclubEventType.AddToCart
    };
  }
  if (((_d = supportedEventsConfig.viewCart) == null ? void 0 : _d.enabled) && (bcEventName === AnalyticsTrackingEvent.DTC_OPEN_BUYBOX || bcEventName === AnalyticsTrackingEvent.VIEW_BAG || bcEventName === AnalyticsTrackingEvent.BC_CHECKOUT_OPEN_BAG_PAGE)) {
    return {
      eventName: supportedEventsConfig.viewCart.mappedEventName,
      eventType: BrandclubEventType.ViewCart
    };
  }
  if (((_e = supportedEventsConfig.initiateCheckout) == null ? void 0 : _e.enabled) && (bcEventName === AnalyticsTrackingEvent.INITIATE_CHECKOUT || bcEventName === AnalyticsTrackingEvent.BC_USER_REQUESTED_CHECKOUT)) {
    return {
      eventName: supportedEventsConfig.initiateCheckout.mappedEventName,
      eventType: BrandclubEventType.InitiateCheckout
    };
  }
  if (((_f = supportedEventsConfig.orderConfirmation) == null ? void 0 : _f.enabled) && bcEventName === AnalyticsTrackingEvent.CHECKOUT_INTENT_CREATED) {
    return {
      eventName: supportedEventsConfig.orderConfirmation.mappedEventName,
      eventType: BrandclubEventType.OrderConfirmation
    };
  }
  if (((_g = supportedEventsConfig.orderSubmitted) == null ? void 0 : _g.enabled) && bcEventName === AnalyticsTrackingEvent.CHECKOUT_ORDER_SUBMIT) {
    return {
      eventName: supportedEventsConfig.orderSubmitted.mappedEventName,
      eventType: BrandclubEventType.OrderSubmitted
    };
  }
  if (((_h = supportedEventsConfig.purchaseConfirmed) == null ? void 0 : _h.enabled) && bcEventName === AnalyticsTrackingEvent.CHECKOUT_ORDER_SUCCESS) {
    return {
      eventName: supportedEventsConfig.purchaseConfirmed.mappedEventName,
      eventType: BrandclubEventType.PurchaseConfirmed
    };
  }
  return void 0;
}
function parseEventAndPayload(eventName, payload, supportedEventsConfig) {
  var _a, _b;
  const mappedEvent = mapAppEventToExternalCustomEvent(eventName, supportedEventsConfig);
  if (!(mappedEvent == null ? void 0 : mappedEvent.eventName) || !(mappedEvent == null ? void 0 : mappedEvent.eventType)) {
    return {
      eventName: void 0,
      eventPayload: {}
    };
  }
  const { eventName: externalEventName, eventType } = mappedEvent;
  const convertedPayload = JSON.parse(
    JSON.stringify({
      stacklineSku: (payload == null ? void 0 : payload.sku) || (payload == null ? void 0 : payload.stacklineSku),
      retailerId: payload == null ? void 0 : payload.retailerId,
      retailerSku: payload == null ? void 0 : payload.retailerSku,
      retailPrice: payload == null ? void 0 : payload.retailPrice,
      pageId: payload == null ? void 0 : payload.pageId,
      pageType: payload == null ? void 0 : payload.pageType,
      brandId: payload == null ? void 0 : payload.brandId,
      brandName: payload == null ? void 0 : payload.brandName,
      categoryId: payload == null ? void 0 : payload.categoryId,
      categoryName: payload == null ? void 0 : payload.categoryName,
      subCategoryId: payload == null ? void 0 : payload.subCategoryId,
      subCategoryName: payload == null ? void 0 : payload.subCategoryName,
      jl_id: payload == null ? void 0 : payload.jl_id,
      jl_c: payload == null ? void 0 : payload.jl_c,
      jl_m: payload == null ? void 0 : payload.jl_m,
      jl_s: payload == null ? void 0 : payload.jl_s,
      cartItems: (_a = payload == null ? void 0 : payload.cartItems) == null ? void 0 : _a.map((item) => item && {
        retailerId: item.retailerId,
        retailerSku: item.retailerSku,
        stacklineSku: item.stacklineSku,
        brandId: item.brandId,
        title: item.title || item.productName,
        brandName: item.brandName,
        categoryId: item.categoryId,
        categoryName: item.categoryName,
        subCategoryId: item.subCategoryId,
        subCategoryName: item.subCategoryName,
        retailPrice: item.retailPrice,
        quantity: item.quantity
      }).filter(Boolean),
      checkoutItems: (_b = payload == null ? void 0 : payload.checkoutItems) == null ? void 0 : _b.map((item) => item && {
        retailerId: item.retailerId,
        retailerSku: item.retailerSku,
        stacklineSku: item.stacklineSku,
        brandId: item.brandId,
        title: item.title,
        brandName: item.brandName,
        categoryId: item.categoryId,
        categoryName: item.categoryName,
        subCategoryId: item.subCategoryId,
        subCategoryName: item.subCategoryName,
        retailPrice: item.retailPrice,
        quantity: item.quantity
      }).filter(Boolean),
      checkoutIntentId: payload == null ? void 0 : payload.checkoutIntentId
    })
  );
  return {
    eventName: externalEventName,
    eventType,
    eventPayload: convertedPayload
  };
}
export {
  parseEventAndPayload,
  timeoutPromise
};
//# sourceMappingURL=utils.js.map
